/**
 * Content Entity Schemas
 *
 * Types for content management (posts, announcements, events).
 */

import { z } from 'zod'

/**
 * Content Type Schema
 *
 * Mirrors the backend `ContentType` Prisma enum + the additional types
 * accepted by the v1 content create endpoint (`QUESTION`, `ALERT`,
 * `ACTION_ITEM`). Keep in sync with
 * `apps/backend/prisma/schema/content.prisma` and
 * `apps/backend/src/api/content/v1/use-cases/(core)/create-single/schema.ts`.
 */
export const ContentTypeSchema = z.enum(['POST', 'ANNOUNCEMENT', 'EVENT', 'NEWS', 'QUESTION', 'ALERT', 'ACTION_ITEM'])
export type ContentType = z.infer<typeof ContentTypeSchema>

/**
 * Content Status Schema
 *
 * Mirrors the v1 content endpoint status states. `READY_TO_PUBLISH` and
 * `SEND_NOW` are write-side terminal-intent states that the backend
 * accepts from create/update; `DRAFT` / `SCHEDULED` are pre-publish.
 * `PUBLISHED` / `ARCHIVED` are read-side observed states kept for
 * compatibility with existing consumers reading `ContentRecord`.
 * `AWAITING_IMPORT` is read-side only — a system-held record converted but not
 * yet drained into Stream by the backlog importer. The backend never accepts it
 * as a write-side input.
 */
export const ContentStatusSchema = z.enum([
  'DRAFT',
  'READY_TO_PUBLISH',
  'SCHEDULED',
  'SEND_NOW',
  'PUBLISHED',
  'ARCHIVED',
  'AWAITING_IMPORT'
])
export type ContentStatus = z.infer<typeof ContentStatusSchema>

/**
 * Notification Mode Schema
 *
 * DB-canonical enum (Prisma `NotificationModes` on `ContentRecord`).
 * Mirrors `apps/backend/prisma/schema/content.prisma` exactly.
 *
 * Decision (2026-05-16): aligned on the DB-persisted enum, NOT the
 * narrower internal notification-subsystem set (`NORMAL|URGENT|CRITICAL|SILENT`).
 * The DB is source of truth — what update writes to `notification_mode`
 * is what every reader sees. The notification subsystem maps the
 * persisted value down to its internal priority enum at send time
 * (see `apps/backend/src/services/content/crud/create-content.ts#resolveNotificationMode`).
 */
export const NotificationModeSchema = z.enum(['NONE', 'NORMAL', 'ALL', 'EMERGENCY'])
export type NotificationMode = z.infer<typeof NotificationModeSchema>

/**
 * Banner Mode Schema
 *
 * Controls how the cover image for posts produced by an inbound-email
 * publishing rule is resolved at render time.
 *
 * - AUTO: pick the first significant image from the email body (legacy behaviour).
 * - CUSTOM: use the rule's `customCoverImageUrl` for every post produced by this rule.
 * - NONE: no cover image.
 */
export const BannerModeSchema = z.enum(['AUTO', 'CUSTOM', 'NONE'])
export type BannerMode = z.infer<typeof BannerModeSchema>

/**
 * Content Category Schema
 */
export const ContentCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  description: z.string().optional()
})

export type ContentCategory = z.infer<typeof ContentCategorySchema>

/**
 * Content Author Schema
 */
export const ContentAuthorSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  photoUrl: z.string().nullable().optional()
})

export type ContentAuthor = z.infer<typeof ContentAuthorSchema>

/**
 * Content Record Schema
 */
export const ContentRecordSchema = z.object({
  id: z.string(),
  type: ContentTypeSchema,
  title: z.string(),
  body: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  status: ContentStatusSchema,
  publishedAt: z.string().nullable().optional(),
  scheduledAt: z.string().nullable().optional(),
  author: ContentAuthorSchema.nullable().optional(),
  categories: z.array(ContentCategorySchema).optional(),
  imageUrl: z.string().nullable().optional(),
  schoolId: z.string(),
  // In-app message / home-card fields. presentationStyle null = normal content;
  // HOME_CARD = a home-screen in-app message. See entities/home-card.ts.
  presentationStyle: z.enum(['HOME_CARD']).nullable().optional(),
  // Home-card kind discriminator + link target. cardKind = 'link' is a link
  // card whose tap opens `linkUrl` in the in-app web view. See home-card.ts.
  cardKind: z.enum(['message', 'link']).optional(),
  linkUrl: z.string().nullable().optional(),
  pinned: z.boolean().optional(),
  dismissible: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export type ContentRecord = z.infer<typeof ContentRecordSchema>

// ============================================================================
// Platform News Types (for /v1/schools/:schoolId/platform-news endpoint)
// These are rich news items synced from external platforms like Blackbaud
// ============================================================================

/**
 * Platform User Schema - Creator/Modifier info from external platform
 */
export const PlatformUserSchema = z.object({
  id: z.string().nullable(),
  name: z.string()
})

export type PlatformUser = z.infer<typeof PlatformUserSchema>

/**
 * Platform Media Item Schema - Media attachments on platform news
 * Note: id is optional and computed from platformId for UI convenience
 */
export const PlatformMediaItemSchema = z.object({
  id: z.string().optional(),
  platformId: z.string(),
  url: z.string().nullable(),
  type: z.string().nullable(),
  caption: z.string().nullable(),
  // Additional fields for UI display
  title: z.string().nullable().optional()
})

export type PlatformMediaItem = z.infer<typeof PlatformMediaItemSchema>

/**
 * Platform News Category Schema - Categories with platform-specific metadata
 * Note: id is optional and computed from platformId for UI convenience
 */
export const PlatformNewsCategorySchema = z.object({
  id: z.string().optional(),
  platformId: z.string(),
  name: z.string(),
  type: z.string(),
  primary: z.boolean(),
  publishDate: z.string().nullable()
})

export type PlatformNewsCategory = z.infer<typeof PlatformNewsCategorySchema>

/**
 * Content Record Reference Schema - Associated content records
 * Note: Date fields are strings for JSON serialization compatibility
 */
export const ContentRecordRefSchema = z.object({
  id: z.string(),
  title: z.string().nullable().optional(),
  contentType: z.string(),
  status: z.string(),
  contentTitle: z.string().nullable().optional(),
  contentBody: z.string().nullable().optional(),
  sourceSystem: z.string().optional(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  publisher: z
    .object({
      id: z.string().nullable(),
      name: z.string(),
      photoUrl: z.string().nullable().optional()
    })
    .optional(),
  author: z
    .object({
      id: z.string().nullable(),
      name: z.string(),
      photoUrl: z.string().nullable().optional()
    })
    .optional(),
  /**
   * Free-text byline carried over from the source system (Blackbaud
   * `author`), e.g. "IT Dept" or "Teri Hanley & Brian Kelly". It is NOT a
   * resolvable person, so `publisher`/`author` above cannot represent it —
   * they require a `name` on a real record. Clients render this when no
   * publisher resolves.
   */
  byline: z.string().nullable().optional()
})

export type ContentRecordRef = z.infer<typeof ContentRecordRefSchema>

/**
 * School Reference Schema
 */
export const SchoolRefSchema = z.object({
  id: z.string(),
  name: z.string()
})

export type SchoolRef = z.infer<typeof SchoolRefSchema>

/**
 * Platform News Item Schema - Full news item from platform sync
 * This is returned by GET /v1/schools/:schoolId/platform-news
 */
export const PlatformNewsItemSchema = z.object({
  id: z.string(),
  school: SchoolRefSchema,
  platformId: z.string(),
  title: z.string().nullable(),
  headline: z.string().nullable(),
  author: ContentAuthorSchema.nullable(),
  shortDescription: z.string().nullable(),
  longDescription: z.string().nullable(),
  featured: z.boolean(),
  categories: z.array(PlatformNewsCategorySchema),
  mediaItems: z.array(PlatformMediaItemSchema),
  sendNotification: z.boolean(),
  showBriefDescription: z.boolean(),
  showLongDescription: z.boolean(),
  platformCreatedDate: z.coerce.date().nullable(),
  platformModifiedDate: z.coerce.date().nullable(),
  platformCreator: PlatformUserSchema.optional(),
  platformModifier: PlatformUserSchema.optional(),
  contentRecords: z.array(ContentRecordRefSchema).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable(),
  // Additional computed/derived fields used by web app
  platformPublishDate: z.string().nullable().optional()
})

export type PlatformNewsItem = z.infer<typeof PlatformNewsItemSchema>
